export enum KeyboardKey {
  'enter' = 'enter',
  'space' = 'space',
  'tab' = 'tab',
  'tab-shift' = 'tab-shift',
  'tab+shift' = 'tab+shift',
  'esc' = 'esc',
  'home' = 'home',
  'end' = 'end',
  'pageUp' = 'pageUp',
  'pageDown' = 'pageDown',
  'arrowUp' = 'arrowUp',
  'arrowDown' = 'arrowDown',
  'arrowLeft' = 'arrowLeft',
  'arrowRight' = 'arrowRight',
  'printableChar' = 'printableChar',
}

type KeyCheck = { key?: string; code?: string };

const keyMatcher = (e: KeyboardEvent, expected: KeyCheck): boolean =>
  e.key === expected.key || e.code === expected.code;

export const keyUtil = {
  isRightShift: (e: KeyboardEvent) =>
    keyMatcher(e, { key: 'Shift', code: 'ShiftRight' }),
  isLeftShift: (e: KeyboardEvent) =>
    keyMatcher(e, { key: 'Shift', code: 'ShiftLeft' }),
  isSpace: (e: KeyboardEvent) => keyMatcher(e, { key: ' ', code: 'Space' }),
  isEnter: (e: KeyboardEvent) => keyMatcher(e, { key: 'Enter', code: 'Enter' }),
  isEscape: (e: KeyboardEvent) =>
    keyMatcher(e, { key: 'Escape', code: 'Escape' }),
  isTab: (e: KeyboardEvent) => keyMatcher(e, { key: 'Tab', code: 'Tab' }),
  isHome: (e: KeyboardEvent) => keyMatcher(e, { key: 'Home', code: 'Home' }),
  isEnd: (e: KeyboardEvent) => keyMatcher(e, { key: 'End', code: 'End' }),
  isPageUp: (e: KeyboardEvent) =>
    keyMatcher(e, { key: 'PageUp', code: 'PageUp' }),
  isPageDown: (e: KeyboardEvent) =>
    keyMatcher(e, { key: 'PageDown', code: 'PageDown' }),
  isArrowLeft: (e: KeyboardEvent) =>
    keyMatcher(e, { key: 'ArrowLeft', code: 'ArrowLeft' }),
  isArrowUp: (e: KeyboardEvent) =>
    keyMatcher(e, { key: 'ArrowUp', code: 'ArrowUp' }),
  isArrowRight: (e: KeyboardEvent) =>
    keyMatcher(e, { key: 'ArrowRight', code: 'ArrowRight' }),
  isArrowDown: (e: KeyboardEvent) =>
    keyMatcher(e, { key: 'ArrowDown', code: 'ArrowDown' }),
  isPrintableCharacter: (e: KeyboardEvent) =>
    e.key.length === 1 && /\S/.test(e.key),
};

/**
 * Determines if the given key is pressed or not
 * @param event
 * @param key Lowercased code of the key (like enter, space, tab)
 * @returns
 */
export function isPressedKey(event: KeyboardEvent, key: KeyboardKey): boolean {
  let shiftKey: boolean | undefined = undefined;
  if (key.endsWith('+shift')) {
    shiftKey = true;
    key = key.replace('+shift', '') as KeyboardKey;
  } else if (key.endsWith('-shift')) {
    shiftKey = false;
    key = key.replace('-shift', '') as KeyboardKey;
  }

  if (shiftKey !== undefined && shiftKey !== event.shiftKey) {
    return false;
  }

  switch (key) {
    case 'enter':
      return keyUtil.isEnter(event);
    case 'space':
      return keyUtil.isSpace(event);
    case 'esc':
      return keyUtil.isEscape(event);
    case 'home':
      return keyUtil.isHome(event);
    case 'end':
      return keyUtil.isEnd(event);
    case 'pageUp':
      return keyUtil.isPageUp(event);
    case 'pageDown':
      return keyUtil.isPageDown(event);
    case 'arrowUp':
      return keyUtil.isArrowUp(event);
    case 'arrowDown':
      return keyUtil.isArrowDown(event);
    case 'arrowLeft':
      return keyUtil.isArrowLeft(event);
    case 'arrowRight':
      return keyUtil.isArrowRight(event);
    case 'tab':
      return keyUtil.isTab(event);
    case 'printableChar':
      return keyUtil.isPrintableCharacter(event);
    default:
      return false;
  }
}

/**
 * Determines if any of the given keyUtil is pressed or not
 * @param event
 * @param keyUtilToCheck keyUtil to check. Use `"+shift"` or `"-shift"` suffix to check if Shift key is also pressed / not pressed (like `"tab+shift"` = true if both Tab and Shift is pressed).
 * @returns
 */
export function checkPressedKey(
  event: KeyboardEvent,
  keyUtilToCheck: KeyboardKey | KeyboardKey[],
): boolean {
  if (!Array.isArray(keyUtilToCheck)) {
    keyUtilToCheck = [keyUtilToCheck];
  }

  let pressed = false;
  let i = 0;
  while (!pressed && i < keyUtilToCheck.length) {
    pressed = isPressedKey(event, keyUtilToCheck[i]);
    ++i;
  }

  return pressed;
}
